import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["input", "messages", "entityCount", "maskedItems", "securityPanel", "sendButton", "detailsText", "modelSelect", "chatgptModal", "maskedPromptBox", "copyButtonText", "chatgptResponseInput", "chatgptIframe", "iframeFallback"]

  connect() {
    console.log("Incognito Chats controller connected")
    this.detailsOpen = false
    this.currentMaskedPrompt = null
    this.currentMapping = null
    
    // Set up ActionCable subscription
    this.setupActionCable()
    
    // Set up iframe error handling
    if (this.hasChatgptIframeTarget) {
      this.chatgptIframeTarget.addEventListener('error', () => this.handleIframeError())
      // Check if iframe loads successfully
      setTimeout(() => this.checkIframeLoad(), 3000)
    }
  }

  disconnect() {
    // Clean up ActionCable subscription
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  setupActionCable() {
    // Get session ID from meta tag
    const sessionIdMeta = document.querySelector('meta[name="session-id"]')
    const sessionId = sessionIdMeta ? sessionIdMeta.content : null
    
    console.log("Session ID for ActionCable:", sessionId)
    
    this.subscription = consumer.subscriptions.create({
      channel: "ChatChannel",
      session_id: sessionId
    }, {
      connected: () => {
        console.log("Connected to ChatChannel with session:", sessionId)
      },

      disconnected: () => {
        console.log("Disconnected from ChatChannel")
      },

      received: (data) => {
        console.log("Received data from ChatChannel:", data)
        this.handleCableMessage(data)
      }
    })
  }

  handleCableMessage(data) {
    if (data.type === 'chat_response') {
      this.handleChatResponse(data.data)
    } else if (data.type === 'chat_error') {
      this.handleChatError(data.data)
    }
  }

  handleChatResponse(data) {
    // Hide loading before adding the response
    this.hideLoading()
    
    // Store the masked prompt and mapping for ChatGPT integration
    this.currentMaskedPrompt = data.cloaked_prompt
    this.currentMapping = data.mapping
    
    // Update security panel if there are entities
    if (data.extracted_entities && data.extracted_entities.length > 0) {
      this.updateSecurityPanel(data)
      
      // Show ChatGPT integration option
      this.showChatGPTOption()
    }
    
    // Add AI response to chat
    this.addMessage(data.final_response, 'assistant', data.sensitive_positions, data.model_used)
    
    // Re-enable button
    this.sendButtonTarget.disabled = false
    this.inputTarget.focus()
  }

  handleChatError(data) {
    console.error('Chat processing error:', data.error)
    this.hideLoading()
    this.addMessage('Sorry, there was an error processing your message. Please try again.', 'assistant error')
    
    // Re-enable button
    this.sendButtonTarget.disabled = false
    this.inputTarget.focus()
  }

  async sendMessage(event) {
    event.preventDefault()
    
    const message = this.inputTarget.value.trim()
    if (!message) return
    
    // Get selected model
    const selectedModel = this.modelSelectTarget.value
    
    // Add user message to chat
    this.addMessage(message, 'user')
    
    // Clear input and reset height
    this.inputTarget.value = ''
    this.inputTarget.style.height = 'auto'
    
    // Disable send button
    this.sendButtonTarget.disabled = true
    
    // Show loading after the user message
    this.showLoading()
    
    try {
      // Send message to backend to start background job
      const response = await fetch('/incognito-chats/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ 
          prompt: message,
          model: selectedModel 
        })
      })
      
      if (!response.ok) throw new Error('Network response was not ok')
      
      const data = await response.json()
      
      if (data.error) {
        throw new Error(data.error)
      }
      
      console.log('Job enqueued:', data.job_id)
      // The response will come via ActionCable, so we just wait
      
    } catch (error) {
      console.error('Error:', error)
      this.hideLoading()
      this.addMessage('Sorry, there was an error processing your message. Please try again.', 'assistant error')
      // Re-enable button
      this.sendButtonTarget.disabled = false
      this.inputTarget.focus()
    }
  }
  
  showChatGPTOption() {
    // Add a message with ChatGPT integration option
    const optionDiv = document.createElement('div')
    optionDiv.className = 'pc-message system'
    optionDiv.innerHTML = `
      <div class="pc-chatgpt-option">
        <p>Your prompt has been processed with sensitive data masked. Would you like to use it with ChatGPT?</p>
        <button class="pc-chatgpt-button" data-action="click->prompt-cloaker#showChatGPTModal">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
            <polyline points="15 3 21 3 21 9"></polyline>
            <line x1="10" y1="14" x2="21" y2="3"></line>
          </svg>
          Use with ChatGPT
        </button>
      </div>
    `
    this.messagesTarget.appendChild(optionDiv)
  }
  
  showChatGPTModal() {
    if (!this.currentMaskedPrompt) return
    
    // Update modal with masked prompt
    this.maskedPromptBoxTarget.textContent = this.currentMaskedPrompt
    
    // Show modal
    this.chatgptModalTarget.style.display = 'flex'
    
    // Reset iframe src to trigger reload
    if (this.hasChatgptIframeTarget) {
      this.chatgptIframeTarget.src = 'https://chat.openai.com/'
    }
  }
  
  closeChatGPTModal() {
    this.chatgptModalTarget.style.display = 'none'
    this.copyButtonTextTarget.textContent = 'Copy Masked Prompt'
  }
  
  async copyMaskedPrompt() {
    if (!this.currentMaskedPrompt) return
    
    try {
      await navigator.clipboard.writeText(this.currentMaskedPrompt)
      this.copyButtonTextTarget.textContent = 'Copied!'
      
      // Reset text after 2 seconds
      setTimeout(() => {
        this.copyButtonTextTarget.textContent = 'Copy Masked Prompt'
      }, 2000)
    } catch (err) {
      console.error('Failed to copy text:', err)
      alert('Failed to copy to clipboard. Please select and copy manually.')
    }
  }
  
  openChatGPT() {
    window.open('https://chat.openai.com/', '_blank')
  }
  
  async restoreChatGPTResponse() {
    const chatgptResponse = this.chatgptResponseInputTarget.value.trim()
    if (!chatgptResponse || !this.currentMapping) return
    
    try {
      // Send the ChatGPT response to backend for restoration
      const response = await fetch('/incognito-chats/restore', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ 
          response_text: chatgptResponse,
          mapping: this.currentMapping
        })
      })
      
      if (!response.ok) throw new Error('Network response was not ok')
      
      const data = await response.json()
      
      // Close modal
      this.closeChatGPTModal()
      
      // Clear the input
      this.chatgptResponseInputTarget.value = ''
      
      // Add ChatGPT's restored response to chat
      this.addMessage(data.restored_text, 'assistant', data.sensitive_positions, 'ChatGPT')
      
    } catch (error) {
      console.error('Error:', error)
      alert('Error restoring response. Please try again.')
    }
  }
  
  addMessage(content, type, sensitivePositions = [], modelUsed = null) {
    const messageDiv = document.createElement('div')
    messageDiv.className = `pc-message ${type}`
    
    const contentDiv = document.createElement('div')
    contentDiv.className = 'pc-message-content'
    
    if (type === 'user') {
      contentDiv.innerHTML = `<p>${this.escapeHtml(content)}</p>`
    } else {
      // Parse markdown and highlight sensitive data for assistant messages
      let processedContent = this.parseMarkdown(content)
      
      // If we have sensitive positions, highlight them
      if (sensitivePositions && sensitivePositions.length > 0) {
        processedContent = this.highlightSensitiveData(processedContent, sensitivePositions)
      }
      
      contentDiv.innerHTML = `<p>${processedContent}</p>`
      
      // Add model indicator for assistant messages
      if (modelUsed && type === 'assistant') {
        const modelIndicator = document.createElement('div')
        modelIndicator.className = 'pc-model-indicator'
        modelIndicator.innerHTML = `<span class="pc-model-name">${this.formatModelName(modelUsed)}</span>`
        contentDiv.appendChild(modelIndicator)
      }
    }
    
    messageDiv.appendChild(contentDiv)
    this.messagesTarget.appendChild(messageDiv)
    
    // Hide all tooltips after adding the message
    setTimeout(() => {
      this.hideAllTooltips()
    }, 100)
    
    // Smooth scroll to bottom
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTo({
        top: this.messagesTarget.scrollHeight,
        behavior: 'smooth'
      })
    })
  }
  
  parseMarkdown(text) {
    // Simple markdown parser for common formatting
    return text
      // Bold text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Italic text
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Code blocks
      .replace(/```(.+?)```/gs, '<code class="block">$1</code>')
      // Inline code
      .replace(/`(.+?)`/g, '<code>$1</code>')
      // Line breaks
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>')
  }
  
  updateSecurityPanel(data) {
    // Update entity count
    const count = data.extracted_entities.length
    this.entityCountTarget.textContent = `${count} ${count === 1 ? 'item' : 'items'}`
    
    // Update masked items
    this.maskedItemsTarget.innerHTML = ''
    
    data.extracted_entities.forEach(entity => {
      const item = document.createElement('div')
      item.className = 'pc-masked-item'
      
      const badge = document.createElement('span')
      badge.className = `pc-entity-badge ${entity.type}`
      badge.textContent = this.formatEntityType(entity.type)
      
      const mapping = document.createElement('div')
      mapping.className = 'pc-entity-mapping'
      mapping.innerHTML = `
        <span class="pc-original">${this.escapeHtml(entity.value)}</span>
        <span class="pc-arrow">→</span>
        <span class="pc-masked">${this.escapeHtml(data.mapping[entity.value])}</span>
      `
      
      item.appendChild(badge)
      item.appendChild(mapping)
      this.maskedItemsTarget.appendChild(item)
    })
    
    // Auto-show panel if not already visible and we have entities
    if (!this.detailsOpen && count > 0) {
      this.toggleDetails()
    }
  }
  
  toggleDetails() {
    this.detailsOpen = !this.detailsOpen
    
    if (this.detailsOpen) {
      this.securityPanelTarget.style.display = 'block'
      this.detailsTextTarget.textContent = 'Hide details'
    } else {
      this.securityPanelTarget.style.display = 'none'
      this.detailsTextTarget.textContent = 'Show details'
    }
  }
  
  adjustTextareaHeight(event) {
    const textarea = event.target
    textarea.style.height = 'auto'
    textarea.style.height = textarea.scrollHeight + 'px'
  }

  handleKeydown(event) {
    // If Enter is pressed without Shift, submit the form
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      
      // Create a submit event and dispatch it to the form
      const form = event.target.closest('form')
      if (form) {
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true })
        form.dispatchEvent(submitEvent)
      }
    }
    // If Shift+Enter, allow default behavior (new line)
  }
  
  showLoading() {
    // Create a new loading message and append it after the last message
    const loadingDiv = document.createElement('div')
    loadingDiv.className = 'pc-message assistant pc-loading-message'
    loadingDiv.setAttribute('data-loading', 'true')
    loadingDiv.innerHTML = `
      <div class="pc-message-content">
        <div class="pc-loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    `
    
    this.messagesTarget.appendChild(loadingDiv)
    
    // Scroll to show the loading indicator
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTo({
        top: this.messagesTarget.scrollHeight,
        behavior: 'smooth'
      })
    })
  }
  
  hideLoading() {
    // Remove any loading messages
    const loadingMessages = this.messagesTarget.querySelectorAll('[data-loading="true"]')
    loadingMessages.forEach(msg => msg.remove())
  }
  
  formatEntityType(type) {
    const typeMap = {
      'name': 'Name',
      'email': 'Email',
      'phone': 'Phone',
      'bank_account': 'Bank Account',
      'financial_amount': 'Amount',
      'routing_number': 'Routing #',
      'ssn': 'SSN',
      'credit_card': 'Card',
      'medical': 'Medical',
      'address': 'Address',
      'company': 'Company'
    }
    
    return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1)
  }
  
  formatModelName(model) {
    const modelNames = {
      'mistral-nemo': 'Mistral Nemo',
      'gpt-3.5-turbo': 'GPT-3.5 Turbo',
      'claude-instant': 'Claude Instant'
    }
    return modelNames[model] || model
  }
  
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
  
  highlightSensitiveData(html, positions) {
    if (!positions || positions.length === 0) return html
    
    // Create a temporary container
    const container = document.createElement('div')
    container.innerHTML = html
    
    // Create a map of text to replace
    const replacements = new Map()
    positions.forEach(pos => {
      const key = pos.original
      if (!replacements.has(key)) {
        replacements.set(key, {
          type: pos.type,
          permutated: pos.permutated || 'cloaked data',
          original: pos.original
        })
      }
    })
    
    // Process all text nodes
    this.processTextNodes(container, replacements)
    
    return container.innerHTML
  }
  
  processTextNodes(node, replacements) {
    if (node.nodeType === Node.TEXT_NODE) {
      let text = node.textContent
      let hasReplacement = false
      
      // Check if this text contains any sensitive data
      replacements.forEach((data, originalText) => {
        if (text.includes(originalText)) {
          hasReplacement = true
        }
      })
      
      if (hasReplacement) {
        // Create a span to hold the processed content
        const span = document.createElement('span')
        let lastIndex = 0
        
        // Sort replacements by position in text to process in order
        const sortedReplacements = Array.from(replacements.entries())
          .filter(([originalText]) => text.includes(originalText))
          .sort((a, b) => text.indexOf(a[0]) - text.indexOf(b[0]))
        
        sortedReplacements.forEach(([originalText, data]) => {
          const index = text.indexOf(originalText, lastIndex)
          if (index !== -1) {
            // Add text before the sensitive data
            if (index > lastIndex) {
              span.appendChild(document.createTextNode(text.substring(lastIndex, index)))
            }
            
            // Create the sensitive data element
            const sensitiveSpan = document.createElement('span')
            sensitiveSpan.className = 'pc-sensitive-data'
            sensitiveSpan.setAttribute('data-type', data.type)
            sensitiveSpan.appendChild(document.createTextNode(originalText))
            
            // Add info icon
            const infoSpan = document.createElement('span')
            infoSpan.className = 'pc-sensitive-info'
            infoSpan.innerHTML = `<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm1 13H7v-2h2v2zm0-3H7V4h2v6z"/></svg><span class="pc-tooltip"><div class="pc-tooltip-content"><div class="pc-tooltip-label">Cloaked during processing</div><div class="pc-tooltip-original">Original: ${this.escapeHtml(originalText)}</div><div class="pc-tooltip-masked">Masked as: ${this.escapeHtml(data.permutated)}</div></div></span>`
            
            // Add simple hover event listeners
            infoSpan.addEventListener('mouseenter', () => {
              const tooltip = infoSpan.querySelector('.pc-tooltip')
              if (tooltip) {
                tooltip.style.display = 'block'
              }
            })
            
            infoSpan.addEventListener('mouseleave', () => {
              const tooltip = infoSpan.querySelector('.pc-tooltip')
              if (tooltip) {
                tooltip.style.display = 'none'
              }
            })
            
            sensitiveSpan.appendChild(infoSpan)
            span.appendChild(sensitiveSpan)
            
            lastIndex = index + originalText.length
          }
        })
        
        // Add remaining text
        if (lastIndex < text.length) {
          span.appendChild(document.createTextNode(text.substring(lastIndex)))
        }
        
        // Replace the text node with our new span
        node.parentNode.replaceChild(span, node)
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Don't process if this is already a sensitive data element
      if (!node.classList || !node.classList.contains('pc-sensitive-data')) {
        // Process child nodes
        const children = Array.from(node.childNodes)
        children.forEach(child => this.processTextNodes(child, replacements))
      }
    }
  }
  
  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }
  
  handleIframeError() {
    console.log('iframe failed to load ChatGPT')
    this.showIframeFallback()
  }
  
  checkIframeLoad() {
    // If the modal is open and we can't access the iframe content, show fallback
    if (this.chatgptModalTarget.style.display === 'flex') {
      try {
        // This will throw an error if we can't access the iframe due to CORS
        const iframeDoc = this.chatgptIframeTarget.contentDocument || this.chatgptIframeTarget.contentWindow.document
        if (!iframeDoc || iframeDoc.body.innerHTML === '') {
          this.showIframeFallback()
        }
      } catch (e) {
        // CORS error - ChatGPT doesn't allow iframe embedding
        this.showIframeFallback()
      }
    }
  }
  
  showIframeFallback() {
    if (this.hasChatgptIframeTarget) {
      this.chatgptIframeTarget.style.display = 'none'
    }
    if (this.hasIframeFallbackTarget) {
      this.iframeFallbackTarget.style.display = 'flex'
    }
  }
  
  useExample(event) {
    const exampleType = event.currentTarget.dataset.example
    const examples = {
      financial: "My bank account number is 1234567890 and I have $50,000 in savings. What's the best investment strategy?",
      medical: "I'm John Doe, DOB 01/15/1985, taking Metformin for diabetes. What lifestyle changes should I make?",
      business: "My company ABC Corp made $2M last year. Our main client is XYZ Ltd. How can we expand?",
      personal: "I'm Sarah Johnson, living at 123 Main St, NYC. My email is sarah@email.com. Need career advice."
    }
    
    if (examples[exampleType]) {
      this.inputTarget.value = examples[exampleType]
      this.adjustTextareaHeight({ target: this.inputTarget })
      this.inputTarget.focus()
    }
  }
  
  // Add method to aggressively hide all tooltips
  hideAllTooltips() {
    const tooltips = document.querySelectorAll('.pc-tooltip')
    tooltips.forEach(tooltip => {
      tooltip.style.display = 'none'
    })
  }
} 